// supabase.js — Forthius Wealth Dashboard
// Full data layer: families, clients, holdings, holding_owners,
// watchlist, bt_assets, client_logins, transactions, profiles
import { createClient } from '@supabase/supabase-js';
const SUPABASE_URL = 'https://aelgwwapsldljmedjwvj.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4';
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
// ─── FAMILIES ───────────────────────────────────────────────
export async function fetchFamilies() {
  const { data, error } = await supabase.from('families').select('*').order('created_at');
  if (error) throw error;
  return data.map(f => ({ id: f.id, name: f.name, emoji: f.emoji || '👨‍👩‍👧', color: f.color || '#2563EB' }));
}
export async function addFamily(family) {
  const { data, error } = await supabase.from('families').insert([{
    id: family.id,
    name: family.name,
    emoji: family.emoji || '👨‍👩‍👧',
  }]).select().single();
  if (error) { console.error('[Supabase] addFamily failed:', error); throw error; }
  return data;
}
export async function deleteFamily(id) {
  const { error } = await supabase.from('families').delete().eq('id', id);
  if (error) throw error;
}
// ─── CLIENTS ────────────────────────────────────────────────
export async function fetchClients() {
  const { data, error } = await supabase.from('clients').select('*').order('created_at');
  if (error) throw error;
  return data.map(c => ({
    id: c.id,
    familyId: c.family_id,
    name: c.name,
    role: c.role || 'Primary',
    email: c.email || '',
    avatar: c.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
    dob: c.dob || null,
    riskProfile: c.risk_profile || 'Moderate',
    signupSource: c.signup_source || null,
    status: c.status || null,
  }));
}
export async function addClient(client) {
  const { error } = await supabase.from('clients').insert([{
    id: client.id,
    family_id: client.familyId,
    name: client.name,
    role: client.role || 'Primary',
    email: client.email || null,
    risk_profile: client.riskProfile || 'Moderate',
    dob: client.dob || null,
    signup_source: client.signupSource || null,
    status: client.status || null,
  }]);
  if (error && error.code !== 'PGRST116') throw error;
  return client;
}
export async function deleteClient(id) {
  const { error } = await supabase.from('clients').delete().eq('id', id);
  if (error) throw error;
}
// ─── HOLDINGS ───────────────────────────────────────────────
export async function fetchHoldings() {
  const { data, error } = await supabase.from('holdings').select('*').order('created_at');
  if (error) throw error;
  return data.map(h => ({
    id: h.id,
    name: h.name,
    ticker: h.ticker || '-',
    class: h.class,
    exchange: h.exchange || 'NYSE',
    currency: h.currency || 'USD',
    qty: parseFloat(h.qty) || 0,
    costPrice: parseFloat(h.cost_price) || 0,
    currentPrice: parseFloat(h.current_price) || 0,
    country: h.country || 'USA',
    listed: h.listed !== false,
    isin: h.isin || '-',
    manualPriceDate: h.manual_price_date || null,
    capSize: h.cap_size || '-',
    sector: h.sector || 'Other',
    manager: h.manager || 'Self-Managed',
    schemeCode: h.scheme_code || null,
    isMutualFund: h.is_mutual_fund || false,
    notes: h.notes || null,
    yahooTicker: h.yahoo_ticker || null,
    isGlobalMF: h.is_global_mf || false,
    reconciledFromTxns: h.reconciled_from_txns || false,
  }));
}
export async function addHolding(holding) {
  const { error } = await supabase.from('holdings').insert([{
    id: String(holding.id),
    name: holding.name,
    ticker: holding.ticker || '-',
    class: holding.class,
    exchange: holding.exchange || 'NYSE',
    currency: holding.currency || 'USD',
    qty: holding.qty || 0,
    cost_price: holding.costPrice || 0,
    current_price: holding.currentPrice || 0,
    country: holding.country || 'USA',
    listed: holding.listed !== false,
    isin: holding.isin || '-',
    manual_price_date: holding.manualPriceDate || null,
    cap_size: holding.capSize || '-',
    sector: holding.sector || 'Other',
    manager: holding.manager || 'Self-Managed',
    scheme_code: holding.schemeCode || null,
    is_mutual_fund: holding.isMutualFund || false,
    notes: holding.notes || null,
    yahoo_ticker: holding.yahooTicker || null,
    is_global_mf: holding.isGlobalMF || false,
    reconciled_from_txns: holding.reconciledFromTxns || false,
  }]);
  if (error && error.code !== 'PGRST116') throw error;
  return holding;
}
export async function updateHolding(id, fields) {
  const dbFields = {};
  if (fields.name               !== undefined) dbFields.name                 = fields.name;
  if (fields.ticker             !== undefined) dbFields.ticker               = fields.ticker;
  if (fields.class              !== undefined) dbFields.class                = fields.class;
  if (fields.exchange           !== undefined) dbFields.exchange             = fields.exchange;
  if (fields.currency           !== undefined) dbFields.currency             = fields.currency;
  if (fields.qty                !== undefined) dbFields.qty                  = fields.qty;
  if (fields.costPrice          !== undefined) dbFields.cost_price           = fields.costPrice;
  if (fields.currentPrice       !== undefined) dbFields.current_price        = fields.currentPrice;
  if (fields.country            !== undefined) dbFields.country              = fields.country;
  if (fields.listed             !== undefined) dbFields.listed               = fields.listed;
  if (fields.isin               !== undefined) dbFields.isin                 = fields.isin;
  if (fields.capSize            !== undefined) dbFields.cap_size             = fields.capSize;
  if (fields.sector             !== undefined) dbFields.sector               = fields.sector;
  if (fields.manager            !== undefined) dbFields.manager              = fields.manager;
  if (fields.schemeCode         !== undefined) dbFields.scheme_code          = fields.schemeCode;
  if (fields.isMutualFund       !== undefined) dbFields.is_mutual_fund       = fields.isMutualFund;
  if (fields.notes              !== undefined) dbFields.notes                = fields.notes;
  if (fields.manualPriceDate    !== undefined) dbFields.manual_price_date    = fields.manualPriceDate;
  if (fields.yahooTicker        !== undefined) dbFields.yahoo_ticker         = fields.yahooTicker;
  if (fields.isGlobalMF         !== undefined) dbFields.is_global_mf         = fields.isGlobalMF;
  if (fields.reconciledFromTxns !== undefined) dbFields.reconciled_from_txns = fields.reconciledFromTxns;
  const { error } = await supabase.from('holdings').update(dbFields).eq('id', String(id));
  if (error) throw error;
}
export async function updateHoldingPrice(id, currentPrice, manualPriceDate) {
  const update = { current_price: currentPrice };
  if (manualPriceDate) update.manual_price_date = manualPriceDate;
  const { error } = await supabase.from('holdings').update(update).eq('id', String(id));
  if (error) throw error;
}
export async function deleteHolding(id) {
  const { error } = await supabase.from('holdings').delete().eq('id', String(id));
  if (error) throw error;
}
// ─── HOLDING OWNERS ─────────────────────────────────────────
export async function fetchHoldingOwners() {
  const { data, error } = await supabase.from('holding_owners').select('*');
  if (error) throw error;
  const map = {};
  data.forEach(row => { map[row.holding_id] = row.client_id; });
  return map;
}
export async function setHoldingOwner(holdingId, clientId) {
  await supabase.from('holding_owners').delete().eq('holding_id', String(holdingId));
  const { error } = await supabase.from('holding_owners').insert([{
    holding_id: String(holdingId),
    client_id: String(clientId),
  }]);
  if (error) throw new Error(`setHoldingOwner failed: ${error.message} (code: ${error.code})`);
}
export async function removeHoldingOwner(holdingId) {
  const { error } = await supabase.from('holding_owners').delete().eq('holding_id', String(holdingId));
  if (error) throw error;
}
// ─── WATCHLIST ───────────────────────────────────────────────
export async function fetchWatchlist() {
  const { data, error } = await supabase.from('watchlist').select('*').order('created_at');
  if (error) throw error;
  return data.map(w => ({
    id: w.id,
    ticker: w.ticker,
    name: w.name || '',
    exchange: w.exchange || 'NASDAQ',
    currency: w.currency || 'USD',
    targetPrice: parseFloat(w.target_price) || 0,
    targetBuy: parseFloat(w.target_buy) || 0,
    alertAbove: w.alert_above || false,
    alertBelow: w.alert_below || false,
    notes: w.notes || '',
    currentPrice: parseFloat(w.current_price) || 0,
    change1d: parseFloat(w.change_1d) || 0,
  }));
}
export async function addWatchlistItem(item) {
  const { error } = await supabase.from('watchlist').insert([{
    id: item.id,
    ticker: item.ticker,
    name: item.name || '',
    exchange: item.exchange || 'NASDAQ',
    currency: item.currency || 'USD',
    target_price: item.targetPrice || 0,
    target_buy: item.targetBuy || 0,
    alert_above: item.alertAbove || false,
    alert_below: item.alertBelow || false,
    notes: item.notes || '',
    current_price: item.currentPrice || 0,
    change_1d: item.change1d || 0,
  }]);
  if (error) throw error;
}
export async function updateWatchlistPrice(id, currentPrice, change1d) {
  const { error } = await supabase.from('watchlist').update({
    current_price: currentPrice,
    change_1d: change1d || 0,
  }).eq('id', id);
  if (error) throw error;
}
export async function updateWatchlistItem(id, fields) {
  const dbFields = {};
  if (fields.ticker      !== undefined) dbFields.ticker       = fields.ticker;
  if (fields.name        !== undefined) dbFields.name         = fields.name;
  if (fields.exchange    !== undefined) dbFields.exchange     = fields.exchange;
  if (fields.currency    !== undefined) dbFields.currency     = fields.currency;
  if (fields.targetPrice !== undefined) dbFields.target_price = fields.targetPrice;
  if (fields.targetBuy   !== undefined) dbFields.target_buy   = fields.targetBuy;
  if (fields.alertAbove  !== undefined) dbFields.alert_above  = fields.alertAbove;
  if (fields.alertBelow  !== undefined) dbFields.alert_below  = fields.alertBelow;
  if (fields.notes       !== undefined) dbFields.notes        = fields.notes;
  if (fields.clientId    !== undefined) dbFields.client_id    = fields.clientId;
  if (fields.thesis      !== undefined) dbFields.thesis       = fields.thesis;
  const { error } = await supabase.from('watchlist').update(dbFields).eq('id', id);
  if (error) throw error;
}
export async function deleteWatchlistItem(id) {
  const { error } = await supabase.from('watchlist').delete().eq('id', id);
  if (error) throw error;
}
// ─── BACKTESTING ASSETS ─────────────────────────────────────
export async function fetchBtAssets() {
  const { data, error } = await supabase.from('bt_assets').select('*').order('created_at');
  if (error) throw error;
  return data.map(a => ({
    id: a.id,
    ticker: a.ticker,
    name: a.name || '',
    exchange: a.exchange || 'NASDAQ',
    currency: a.currency || 'USD',
    entryPrice: parseFloat(a.entry_price) || 0,
    exitPrice: parseFloat(a.exit_price) || null,
    currentPrice: parseFloat(a.current_price) || 0,
    amount: parseFloat(a.amount) || 0,
    addedOn: a.added_on || null,
    exitedOn: a.exited_on || null,
    status: a.status || 'active',
    notes: a.notes || '',
    allocPct: parseFloat(a.alloc_pct) || 0,
  }));
}
export async function addBtAsset(asset) {
  const { error } = await supabase.from('bt_assets').insert([{
    id: asset.id,
    ticker: asset.ticker,
    name: asset.name || '',
    exchange: asset.exchange || 'NASDAQ',
    currency: asset.currency || 'USD',
    entry_price: asset.entryPrice || 0,
    exit_price: asset.exitPrice || null,
    current_price: asset.currentPrice || 0,
    amount: asset.amount || 0,
    added_on: asset.addedOn || null,
    exited_on: asset.exitedOn || null,
    status: asset.status || 'active',
    notes: asset.notes || '',
    alloc_pct: asset.allocPct || 0,
  }]);
  if (error) throw error;
}
export async function updateBtAsset(id, fields) {
  const dbFields = {};
  if (fields.exitPrice    !== undefined) dbFields.exit_price    = fields.exitPrice;
  if (fields.exitedOn     !== undefined) dbFields.exited_on     = fields.exitedOn;
  if (fields.status       !== undefined) dbFields.status        = fields.status;
  if (fields.currentPrice !== undefined) dbFields.current_price = fields.currentPrice;
  if (fields.allocPct     !== undefined) dbFields.alloc_pct     = fields.allocPct;
  if (fields.entryPrice   !== undefined) dbFields.entry_price   = fields.entryPrice;
  if (fields.amount       !== undefined) dbFields.amount        = fields.amount;
  if (fields.notes        !== undefined) dbFields.notes         = fields.notes;
  const { error } = await supabase.from('bt_assets').update(dbFields).eq('id', id);
  if (error) throw error;
}
export async function deleteBtAsset(id) {
  const { error } = await supabase.from('bt_assets').delete().eq('id', id);
  if (error) throw error;
}
// ─── CLIENT LOGINS ───────────────────────────────────────────
// Columns: client_id, login_id, password, active, risk_access,
//          last_login, status, created_at, approved_at, email, phone, family_name
export async function fetchClientLogins() {
  const { data, error } = await supabase.from('client_logins').select('*');
  if (error) throw error;
  const map = {};
  data.forEach(row => {
    map[row.client_id] = {
      loginId:    row.login_id,
      password:   row.password,
      active:     row.active !== false,
      riskAccess: row.risk_access || false,
      lastLogin:  row.last_login || null,
      // Approval gate fields
      status:     row.status || null,
      createdAt:  row.created_at || null,
      approvedAt: row.approved_at || null,
      email:      row.email || null,
      phone:      row.phone || null,
      familyName: row.family_name || null,
      trialEnds:  row.trial_ends || null,
    };
  });
  return map;
}
export async function upsertClientLogin(clientId, cred) {
  const { error } = await supabase.from('client_logins').upsert([{
    client_id:   clientId,
    login_id:    cred.loginId,
    password:    cred.password,
    active:      cred.active !== false,
    risk_access: cred.riskAccess || false,
    last_login:  cred.lastLogin || null,
    // Approval gate fields — stored if provided
    status:      cred.status      || null,
    approved_at: cred.approvedAt  || null,
    email:       cred.email       || null,
    phone:       cred.phone       || null,
    family_name: cred.familyName  || null,
    trial_ends:  cred.trialEnds   || null,
  }], { onConflict: 'client_id' });
  if (error) throw error;
}
export async function updateClientLoginLastLogin(clientId) {
  const today = new Date().toISOString().slice(0, 10);
  const { error } = await supabase.from('client_logins')
    .update({ last_login: today })
    .eq('client_id', clientId);
  if (error) throw error;
}
// ─── TRANSACTIONS ────────────────────────────────────────────
// Paginated fetch — Supabase defaults to 1000 rows per request.
// We loop in batches of 1000 until all rows are retrieved so the
// dashboard always sees the full transaction history regardless of size.
export async function fetchTransactions() {
  const allRows = [];
  const PAGE    = 1000;
  let   from    = 0;

  while (true) {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .order('date', { ascending: false })
      .range(from, from + PAGE - 1);

    if (error) throw error;
    if (!data || data.length === 0) break;

    allRows.push(...data);
    if (data.length < PAGE) break; // last page — no more rows
    from += PAGE;
  }

  return allRows.map(t => ({
    id:       t.id,
    date:     t.date,
    holding:  t.holding,
    type:     t.type || 'BUY',
    qty:      parseFloat(t.qty)   || 0,
    price:    parseFloat(t.price) || 0,
    currency: t.currency || 'USD',
    fees:     parseFloat(t.fees)  || 0,
    clientId: t.client_id || null,
  }));
}
export async function addTransaction(txn) {
  const { data, error } = await supabase.from('transactions').insert([{
    id:        txn.id,
    date:      txn.date,
    holding:   txn.holding,
    type:      txn.type || 'BUY',
    qty:       txn.qty   || 0,
    price:     txn.price || 0,
    currency:  txn.currency || 'USD',
    fees:      txn.fees || 0,
    client_id: txn.clientId || null,
  }]).select().single();
  if (error) throw error;
  return data;
}
export async function updateTransaction(id, fields) {
  const dbFields = {};
  if (fields.date     !== undefined) dbFields.date      = fields.date;
  if (fields.holding  !== undefined) dbFields.holding   = fields.holding;
  if (fields.type     !== undefined) dbFields.type      = fields.type;
  if (fields.qty      !== undefined) dbFields.qty       = fields.qty;
  if (fields.price    !== undefined) dbFields.price     = fields.price;
  if (fields.currency !== undefined) dbFields.currency  = fields.currency;
  if (fields.fees     !== undefined) dbFields.fees      = fields.fees;
  if (fields.clientId !== undefined) dbFields.client_id = fields.clientId;
  const { error } = await supabase.from('transactions').update(dbFields).eq('id', id);
  if (error) throw error;
}
export async function deleteTransaction(id) {
  const { error } = await supabase.from('transactions').delete().eq('id', id);
  if (error) throw error;
}
// ─── PROFILES ────────────────────────────────────────────────
export async function fetchProfiles() {
  const { data, error } = await supabase.from('profiles').select('*');
  if (error) throw error;
  return (data || []).reduce((acc, row) => {
    acc[row.user_id] = row.data;
    return acc;
  }, {});
}
export async function upsertProfile(userId, data) {
  const { error } = await supabase.from('profiles')
    .upsert({ user_id: userId, data, updated_at: new Date().toISOString() },
             { onConflict: 'user_id' });
  if (error) throw error;
}
// ─── PRICE SNAPSHOTS ─────────────────────────────────────────
export async function upsertPriceSnapshot(holdingId, price, currency, date) {
  if (!holdingId || !price || price <= 0) return;
  const { error } = await supabase
    .from('price_snapshots')
    .upsert(
      { holding_id: holdingId, snapshot_date: date, price, currency },
      { onConflict: 'holding_id,snapshot_date' }
    );
  if (error) console.warn('[Supabase] upsertPriceSnapshot:', error.message);
}
export async function fetchPriceSnapshots(holdingIds) {
  if (!holdingIds?.length) return {};
  const result = {};
  // Fetch in batches of 10 holdings to avoid row limit issues
  // Each holding has ~61 rows, so 10 × 61 = 610 rows per batch — well under limit
  for (let i = 0; i < holdingIds.length; i += 10) {
    const batch = holdingIds.slice(i, i + 10);
    const { data, error } = await supabase
      .from('price_snapshots')
      .select('holding_id, snapshot_date, price, currency')
      .in('holding_id', batch)
      .order('snapshot_date', { ascending: false })
      .limit(1000);
    if (error) { console.warn('[Supabase] fetchPriceSnapshots:', error.message); continue; }
    (data || []).forEach(row => {
      if (!result[row.holding_id]) result[row.holding_id] = [];
      result[row.holding_id].push({
        date:     row.snapshot_date,
        price:    parseFloat(row.price),
        currency: row.currency,
      });
    });
  }
  return result;
}
